// The receiving station stays signed in as whoever signed in last, so every
// receive asks who is at the bench: a name, then a four-digit PIN.
//
// Rendered as a SIBLING of whatever DrawerShell it sits over, never inside its
// card: DrawerShell portals, and the later portal lands later in <body>, which
// is what puts this one on top at the same z-index.

import React, { useEffect, useRef, useState } from 'react';
import { buttonClass } from '../components/ui/Button';
import { Field } from '../components/ui/Field';
import { Tooltip } from '../components/ui/Tooltip';
import { Alert } from '../components/ui/Alert';
import FadeIn from '../components/ui/FadeIn';
import Reveal from '../components/ui/Reveal';
import { EmptyState } from '../components/ui/EmptyState';
import { SkeletonGroup, SkeletonText } from '../components/ui/Skeleton';
import { useApi } from '../apiHelpers';
import DrawerShell from './DrawerShell';
import UserPicker from '../components/UserPicker';
import { modalLabel } from '../components/review/fieldStyles';
import type { ReceiverIdentity, ReceiverRow } from './types';

interface Props {
  onConfirm: (identity: ReceiverIdentity) => void;
  onClose: () => void;
  /** The server's refusal of the last attempt, shown under the PIN. */
  error?: string | null;
  busy?: boolean;
}

export const LAST_RECEIVER_KEY = 'receiving.lastReceiverId';

// Private browsing and locked-down kiosks throw on access rather than returning
// null, and a remembered name is a convenience that must never block a receive.
function readLastReceiver(): string | null {
  try {
    return window.localStorage.getItem(LAST_RECEIVER_KEY);
  } catch {
    return null;
  }
}

function writeLastReceiver(id: string) {
  try {
    window.localStorage.setItem(LAST_RECEIVER_KEY, id);
  } catch {
    // Remembering is best effort.
  }
}

const fullName = (u: ReceiverRow) => [u.firstname, u.lastname].filter(Boolean).join(' ') || 'Unnamed';

const NO_EXCLUDES: string[] = [];

const PICKER_INPUT: Record<string, unknown> = {
  'data-drawer-autofocus': '',
  placeholder: 'Type your name',
  'aria-label': 'Your name',
};

const TEXT_BUTTON = buttonClass('quiet', 'md');
const FILLED_BUTTON = buttonClass('filled', 'md');

type LoadState = 'loading' | 'ready' | 'empty' | 'failed';

const ReceiverIdentityModal: React.FC<Props> = ({ onConfirm, onClose, error = null, busy = false }) => {
  const api = useApi();
  const [receivers, setReceivers] = useState<ReceiverRow[]>([]);
  const [load, setLoad] = useState<LoadState>('loading');
  const [picked, setPicked] = useState<ReceiverRow | null>(null);
  const [pin, setPin] = useState('');
  const pinRef = useRef<HTMLInputElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const rows = (await api.get('/inventory/receivers')) as ReceiverRow[];
        if (!alive) return;
        const list = Array.isArray(rows) ? rows : [];
        setReceivers(list);
        setLoad(list.length > 0 ? 'ready' : 'empty');
        const lastId = readLastReceiver();
        const last = lastId ? list.find((u) => u.id === lastId) : undefined;
        if (last) setPicked(last);
      } catch {
        if (alive) setLoad('failed');
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // DrawerShell's own autofocus runs once, on mount, before the names arrive.
  // This takes over once there is something to focus.
  useEffect(() => {
    if (load !== 'ready') return;
    const t = setTimeout(() => {
      if (picked) pinRef.current?.focus();
      else pickerRef.current?.querySelector<HTMLInputElement>('input')?.focus();
    }, 0);
    return () => clearTimeout(t);
  }, [load, picked]);

  // A refused PIN is cleared so the retry is four keystrokes, not eight.
  useEffect(() => {
    if (!error) return;
    setPin('');
    pinRef.current?.focus();
  }, [error]);

  const handlePick = (user: ReceiverRow) => {
    setPicked(user);
    setPin('');
    writeLastReceiver(user.id);
  };

  const notYou = () => {
    setPicked(null);
    setPin('');
  };

  // Only a typed PIN: a picked name is remembered the moment it is picked. Never while busy,
  // where the shell's close does nothing and a Discard question would lead nowhere.
  const dirty = !busy && pin !== '';

  const canSubmit = !!picked && /^\d{4}$/.test(pin) && !busy;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!canSubmit || !picked) return;
    onConfirm({ user_id: picked.id, pin, display_name: fullName(picked) });
  };

  return (
    // Closing mid-request would hide the answer to a receive that may already
    // have landed, so the shell ignores Esc and the backdrop while busy.
    <DrawerShell title="Who is receiving this?" size="md" onClose={busy ? () => {} : onClose} dirty={dirty}>
      {load === 'loading' && (
        <SkeletonGroup label="Loading names">
          <SkeletonText lines={2} />
        </SkeletonGroup>
      )}

      {load === 'failed' && (
        <FadeIn>
        <Alert
          tone="error"
          action={<button type="button" onClick={onClose} className={TEXT_BUTTON}>Close</button>}
        >
          Could not load the names. Check the connection and try again.
        </Alert>
        </FadeIn>
      )}

      {load === 'empty' && (
        <FadeIn>
        <EmptyState
          variant="area"
          action={<button type="button" onClick={onClose} className={TEXT_BUTTON}>Close</button>}
        >
          No one here has a PIN yet. Set yours under your profile, then come back.
        </EmptyState>
        </FadeIn>
      )}

      {load === 'ready' && (
        <FadeIn>
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          {picked ? (
            <div className="flex flex-wrap items-center gap-2">
              {/* The one moment of motion: the name landing is the step that
                  matters here, so it settles in rather than appearing. */}
              <span
                key={picked.id}
                className="inline-flex min-w-0 items-center gap-2 rounded-full bg-surface-container-high py-1 pl-1 pr-3 motion-safe:animate-menuIn"
              >
                <span
                  aria-hidden="true"
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/15 text-xs font-semibold text-primary-ink"
                >
                  {(picked.firstname || picked.lastname || '?').charAt(0).toUpperCase()}
                </span>
                <Tooltip label={fullName(picked)} truncated>
                  <span className="truncate text-sm font-medium text-on-surface">
                    {fullName(picked)}
                  </span>
                </Tooltip>
              </span>
              <button
                type="button"
                onClick={notYou}
                disabled={busy}
                className={buttonClass('quiet', 'sm')}
              >
                Not you?
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-on-surface-variant">
                Pick your name, then type your PIN. The receive goes on record under you.
              </p>
              <div ref={pickerRef}>
                <UserPicker
                  users={receivers}
                  excludeIds={NO_EXCLUDES}
                  onSelect={handlePick}
                  inputProps={PICKER_INPUT}
                />
              </div>
              {/* The list holds PIN holders only, so a search that finds nobody
                  needs this sentence or it reads as the app being broken. */}
              <p className="text-xs text-on-surface-variant leading-snug">
                Not finding your name? Only people with a PIN are listed. Set yours under Settings, then come back.
              </p>
            </div>
          )}

          {picked && (
            <label className="block">
              <span className={modalLabel}>Your PIN</span>
              <Field
                ref={pinRef}
                size="lg"
                error={!!error}
                data-drawer-autofocus=""
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={4}
                // Chrome ignores "off" on a password field, so a saved login can
                // land in it; "new-password" is the value it respects.
                autoComplete="new-password"
                data-1p-ignore=""
                data-lpignore="true"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                aria-describedby={error ? 'receiver-pin-error' : undefined}
                className="!max-w-[10rem] text-center text-lg tabular-nums tracking-[0.6em]"
              />
            </label>
          )}

          <Reveal show={!!picked && !!error}>
            <div id="receiver-pin-error">
              <Alert tone="error" variant="text">{error}</Alert>
            </div>
          </Reveal>

          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} disabled={busy} className={TEXT_BUTTON}>
              Cancel
            </button>
            {picked && (
              <button type="submit" disabled={!canSubmit} className={FILLED_BUTTON}>
                {busy ? 'Receiving…' : `Receive as ${picked.firstname || fullName(picked)}`}
              </button>
            )}
          </div>
        </form>
        </FadeIn>
      )}
    </DrawerShell>
  );
};

export default ReceiverIdentityModal;
